import { create } from "zustand";
import {
  expenseApi,
  expenseListQuery,
  type ExpenseCreatePayload,
  type ExpenseEditPayload,
  type ExpenseListParams,
  type ExpenseScheduleCreatePayload,
  type ExpenseScheduleUpdatePayload,
  type ExpenseShipmentMutationInput,
  type ExpenseShipmentRefresh,
} from "../../api/expense/api";
import {
  createExpenseReferenceSlice,
  expenseReferenceInitialState,
  resetExpenseReferenceRequests,
  type ExpenseReferenceSlice,
} from "./referenceSlice";
import type {
  Expense,
  ExpenseAttachment,
  ExpenseCategory,
  ExpenseListResponse,
  ExpenseSchedule,
  ExpenseShipment,
  ExpenseSummary,
  ExpenseConfirmationResult,
  PaymentAccount,
} from "../../expense/types";
import {
  createLatestRequest,
  isAbortError,
} from "./requests";
import { suppressRealtimeEvent } from "../../realtime/suppressRealtime";
import {
  createExpenseListCacheSlice,
  expenseListCacheInitialState,
  expenseListQueryState,
  resetExpenseListCacheRequests,
  withExpenseListQuery,
  type ExpenseListCacheSlice,
} from "./listCacheSlice";
import { resetMerchantReportCache } from "./merchantReportCache";

type ExpenseState = ExpenseReferenceSlice & ExpenseListCacheSlice & {
  expenses: Expense[];
  categories: ExpenseCategory[];
  paymentAccounts: PaymentAccount[];
  schedules: ExpenseSchedule[];
  summary: ExpenseSummary | null;
  selectedExpense: Expense | null;
  activeParams: ExpenseListParams;
  statusTotals: NonNullable<ExpenseListResponse["statusTotals"]>;
  nextCursor: string | null;
  hydrated: boolean;
  loading: boolean;
  loadingMore: boolean;
  detailLoading: boolean;
  summaryLoading: boolean;
  actionLoading: boolean;
  scheduleActionId: string | null;
  scheduleLoadingId: string | null;
  error: string | null;
  detailError: string | null;
  actionError: string | null;
  scheduleError: string | null;
  expenseQueryVersion: number;
  load: (params?: ExpenseListParams, force?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  loadDetail: (id: string, options?: { silent?: boolean }) => Promise<void>;
  loadSchedule: (id: string, force?: boolean) => Promise<ExpenseSchedule>;
  create: (payload: ExpenseCreatePayload) => Promise<Expense>;
  update: (id: string, payload: ExpenseEditPayload) => Promise<Expense>;
  updateDescription: (id: string, description: string) => Promise<void>;
  saveShipments: (
    id: string,
    shipments: ExpenseShipmentMutationInput[],
    existing?: ExpenseShipment[],
    creationOperationId?: string,
  ) => Promise<void>;
  refreshShipment: (
    expenseId: string,
    shipmentId: string,
  ) => Promise<ExpenseShipmentRefresh>;
  restoreShipment: (
    expenseId: string,
    shipmentId: string,
    mode: "alongside" | "replace",
    replaceShipmentId?: string,
  ) => Promise<void>;
  confirm: (id: string, duplicateWarningFingerprint?: string) => Promise<ExpenseConfirmationResult>;
  reject: (id: string) => Promise<void>;
  voidExpense: (id: string) => Promise<void>;
  correct: (id: string) => Promise<Expense>;
  uploadAttachment: (id: string, file: File, refreshDetail?: boolean, creationOperationId?: string) => Promise<ExpenseAttachment>;
  replaceAttachment: (id: string, attachmentId: string, file: File) => Promise<ExpenseAttachment>;
  removeAttachment: (id: string, attachmentId: string) => Promise<void>;
  extractAttachment: (id: string, attachmentId: string) => Promise<void>;
  createSchedule: (payload: ExpenseScheduleCreatePayload) => Promise<ExpenseSchedule>;
  updateSchedule: (
    id: string,
    payload: ExpenseScheduleUpdatePayload,
    options?: { updateDraftExpenses?: boolean },
  ) => Promise<ExpenseSchedule>;
  previewScheduleDraftUpdate: (id: string) => Promise<number>;
  activateSchedule: (id: string) => Promise<ExpenseSchedule>;
  deactivateSchedule: (id: string) => Promise<ExpenseSchedule>;
  retryScheduleGeneration: (id: string) => Promise<ExpenseSchedule>;
  refreshSchedules: () => Promise<void>;
  refresh: () => Promise<void>;
  refreshSummary: () => Promise<void>;
  applyChanged: (expenseId?: string, reason?: string, scheduleId?: string) => Promise<void>;
  clearActionError: () => void;
  reset: () => void;
};

let summaryRequestVersion = 0;
let scheduleRequestVersion = 0;
const listRequest = createLatestRequest();
const moreRequest = createLatestRequest();
const detailRequest = createLatestRequest();
const scheduleRequests = new Map<string, Promise<ExpenseSchedule>>();
const EXPENSE_DETAIL_ONLY_REASONS = new Set([
  "description_edited",
  "attachment_added",
  "attachment_replaced",
  "attachment_removed",
  "extraction_retry",
  "extraction_processing",
  "extraction_completed",
  "extraction_failed",
  "extraction_needs_review",
  "shipment_created",
  "shipment_edited",
  "shipment_removed",
  "shipment_status_changed",
]);
const EXPENSE_SCHEDULE_REASONS = new Set([
  "schedule_created",
  "schedule_edited",
  "schedule_deactivated",
  "schedule_activated",
]);

export const useExpenseStore = create<ExpenseState>((set, get, store) => ({
  expenses: [], categories: [], paymentAccounts: [], schedules: [],
  summary: null, selectedExpense: null, activeParams: {}, statusTotals: [],
  nextCursor: null, hydrated: false, loading: false, loadingMore: false,
  detailLoading: false, summaryLoading: false, actionLoading: false,
  scheduleActionId: null, scheduleLoadingId: null, error: null,
  detailError: null, actionError: null, scheduleError: null,
  expenseQueryVersion: 0,
  ...createExpenseReferenceSlice(set, get, store),
  ...createExpenseListCacheSlice(set, get, store),
  load: async (params = get().activeParams, force = false) => {
    const state = get();
    const queryKey = expenseListQuery(params);
    const queryChanged = queryKey !== expenseListQuery(state.activeParams);
    if (state.hydrated && !queryChanged && !force) return;
    const cachedWidget = state.expenseListQueries[queryKey];
    const reusableWidget =
      !force &&
      cachedWidget?.version === state.expenseQueryVersion &&
      !cachedWidget.error
        ? cachedWidget
        : null;
    moreRequest.reset();
    const request = listRequest.begin();
    set({
      activeParams: params,
      loading: true,
      loadingMore: false,
      error: null,
      ...(!state.hydrated ? { summaryLoading: true } : {}),
      // Keep the last complete page visible while a new query is in flight.
      // The request guard below still prevents stale responses from winning.
      ...(queryChanged ? { nextCursor: null } : {}),
    });
    try {
      const pageRequest = (reusableWidget
        ? Promise.resolve({
            items: reusableWidget.items,
            statusTotals: reusableWidget.statusTotals,
            nextCursor: reusableWidget.nextCursor,
          })
        : expenseApi.list(params, request.signal)
      ).then((expensePage) => {
        if (!request.isCurrent()) return;
        set((latest) => ({
          expenses: expensePage.items,
          statusTotals: expensePage.statusTotals || [],
          nextCursor: expensePage.nextCursor,
          loading: false,
          expenseListQueries: withExpenseListQuery(
            latest.expenseListQueries,
            queryKey,
            expenseListQueryState(params, expensePage, latest.expenseQueryVersion),
          ),
        }));
      });
      if (!state.hydrated) {
        const results = await Promise.allSettled([
          pageRequest,
          Promise.all([
            expenseApi.categories(true),
            expenseApi.paymentAccounts(true),
          ]).then(([categories, paymentAccounts]) => {
            if (request.isCurrent())
              set({ categories, paymentAccounts, referencesHydrated: true });
          }),
          expenseApi.schedules().then((schedules) => {
            if (request.isCurrent()) set({ schedules });
          }),
          expenseApi.summary().then((summary) => {
            if (request.isCurrent()) set({ summary, summaryLoading: false });
          }),
        ]);
        const failure = results.find((result) => result.status === "rejected");
        if (failure?.status === "rejected") throw failure.reason;
        if (request.isCurrent()) set({ hydrated: true });
      } else {
        await pageRequest;
      }
    } catch (error) {
      if (!request.isCurrent()) return;
      if (isAbortError(error)) return;
      set({ error: error instanceof Error ? error.message : "Unable to load expenses" });
      throw error;
    } finally {
      if (request.isCurrent()) set({ loading: false, ...(!state.hydrated ? { summaryLoading: false } : {}) });
      request.finish();
    }
  },
  loadMore: async () => {
    const { activeParams, nextCursor, loading, loadingMore } = get();
    if (!nextCursor || loading || loadingMore) return;
    const queryKey = expenseListQuery(activeParams);
    const cursor = nextCursor;
    const request = moreRequest.begin();
    set({ loadingMore: true, error: null });
    try {
      const page = await expenseApi.list({ ...activeParams, cursor, includeTotal: false }, request.signal);
      const latest = get();
      if (
        !request.isCurrent() ||
        expenseListQuery(latest.activeParams) !== queryKey ||
        latest.nextCursor !== cursor
      ) return;
      set((state) => {
        const existingIds = new Set(state.expenses.map((expense) => expense.id));
        const expenses = [...state.expenses, ...page.items.filter((expense) => !existingIds.has(expense.id))];
        return {
          expenses,
          nextCursor: page.nextCursor,
          expenseListQueries: withExpenseListQuery(
            state.expenseListQueries,
            queryKey,
            expenseListQueryState(activeParams, { items: expenses, statusTotals: state.statusTotals, nextCursor: page.nextCursor }, state.expenseQueryVersion),
          ),
        };
      });
    } catch (error) {
      if (!request.isCurrent() || isAbortError(error) || expenseListQuery(get().activeParams) !== queryKey) return;
      set({ error: error instanceof Error ? error.message : "Unable to load more expenses" });
    }
    finally {
      if (request.isCurrent() && expenseListQuery(get().activeParams) === queryKey)
        set({ loadingMore: false });
      request.finish();
    }
  },
  loadDetail: async (expenseId, options) => {
    const silent = options?.silent ?? false;
    const request = detailRequest.begin();
    if (!silent) set({ detailLoading: true, detailError: null });
    try {
      const expense = await expenseApi.detail(expenseId, request.signal);
      if (request.isCurrent()) set({ selectedExpense: expense });
    }
    catch (error) {
      if (!request.isCurrent() || isAbortError(error)) return;
      if (!silent)
        set({ selectedExpense: null, detailError: error instanceof Error ? error.message : "Unable to load expense" });
      throw error;
    }
    finally {
      if (!silent && request.isCurrent()) set({ detailLoading: false });
      request.finish();
    }
  },
  loadSchedule: async (scheduleId, force = false) => {
    const existing = get().schedules.find((schedule) => schedule.id === scheduleId);
    if (existing?.historySummary && !force) return existing;
    const pending = scheduleRequests.get(scheduleId);
    if (pending && !force) return pending;
    if (pending) {
      await pending.catch(() => undefined);
      const followUp = scheduleRequests.get(scheduleId);
      if (followUp) return followUp;
    }
    const requestVersion = scheduleRequestVersion;
    set({ scheduleLoadingId: scheduleId, scheduleError: null });
    const request = expenseApi
      .schedule(scheduleId)
      .then((schedule) => {
        if (requestVersion === scheduleRequestVersion)
          set((state) => ({
            schedules: replaceSchedule(state.schedules, schedule),
          }));
        return schedule;
      })
      .catch((error) => {
        if (requestVersion === scheduleRequestVersion)
          set({
            scheduleError:
              error instanceof Error ? error.message : "Unable to load schedule",
          });
        throw error;
      })
      .finally(() => {
        scheduleRequests.delete(scheduleId);
        if (
          requestVersion === scheduleRequestVersion &&
          get().scheduleLoadingId === scheduleId
        )
          set({ scheduleLoadingId: null });
      });
    scheduleRequests.set(scheduleId, request);
    return request;
  },
  refresh: async () => {
    set((state) => ({
      expenseQueryVersion: state.expenseQueryVersion + 1,
      categoryReportKey: "",
      paymentAccountReportKey: "",
    }));
    await get().load(get().activeParams, true);
  },
  refreshSummary: async () => {
    const requestVersion = ++summaryRequestVersion;
    const showLoading = !get().summary;
    if (showLoading) set({ summaryLoading: true });
    try {
      const summary = await expenseApi.summary();
      if (requestVersion === summaryRequestVersion) set({ summary });
    } finally {
      if (showLoading && requestVersion === summaryRequestVersion)
        set({ summaryLoading: false });
    }
  },
  create: async (payload) => {
    const expense = await expenseApi.create(payload);
    await refreshExpenseSurfaces(get, expense);
    return expense;
  },
  update: async (expenseId, payload) => {
    suppressRealtimeEvent("expense.changed", expenseId);
    const expense = await expenseApi.update(expenseId, payload);
    await Promise.allSettled([
      refreshExpenseSurfaces(get, expense),
      // Mutation responses can omit expanded detail relationships. Preserve
      // the last complete snapshot until the authoritative detail is ready.
      get().loadDetail(expenseId, { silent: true }),
    ]);
    return expense;
  },
  updateDescription: async (expenseId, description) => {
    suppressRealtimeEvent("expense.changed", expenseId);
    set({ actionLoading: true, actionError: null });
    try {
      const expense = await expenseApi.updateDescription(
        expenseId,
        description,
      );
      set((state) => ({
        selectedExpense: expense,
        expenses: state.expenses.map((item) =>
          item.id === expenseId
            ? { ...item, description: expense.description }
            : item,
        ),
        expenseListQueries: Object.fromEntries(
          Object.entries(state.expenseListQueries).map(([key, query]) => [
            key,
            {
              ...query,
              items: query.items.map((item) =>
                item.id === expenseId
                  ? { ...item, description: expense.description }
                  : item,
              ),
            },
          ]),
        ),
      }));
      if (get().activeParams.search) await get().refresh();
    } catch (error) {
      set({
        actionError:
          error instanceof Error
            ? error.message
            : "Unable to update expense description",
      });
      throw error;
    } finally {
      set({ actionLoading: false });
    }
  },
  saveShipments: async (expenseId, shipments, _existing, creationOperationId) => {
    suppressRealtimeEvent("expense.changed", expenseId);
    await (creationOperationId ? expenseApi.syncShipments(expenseId, shipments, creationOperationId) : expenseApi.syncShipments(expenseId, shipments));
    if (get().selectedExpense?.id === expenseId) await get().loadDetail(expenseId);
  },
  refreshShipment: async (expenseId, shipmentId) => {
    const result = await expenseApi.refreshShipment(expenseId, shipmentId);
    set((state) => ({
      selectedExpense:
        state.selectedExpense?.id === expenseId
          ? {
              ...state.selectedExpense,
              shipments: state.selectedExpense.shipments?.map((shipment) =>
                shipment.id === shipmentId
                  ? {
                      ...shipment,
                      refreshAvailable: result.refreshAvailable,
                      refreshAfter: result.refreshAfter,
                    }
                  : shipment,
              ),
            }
          : state.selectedExpense,
    }));
    return result;
  },
  restoreShipment: async (
    expenseId,
    shipmentId,
    mode,
    replaceShipmentId,
  ) => {
    suppressRealtimeEvent("expense.changed", expenseId);
    await expenseApi.restoreShipment(
      expenseId,
      shipmentId,
      mode,
      replaceShipmentId,
    );
    if (get().selectedExpense?.id === expenseId) await get().loadDetail(expenseId);
  },
  confirm: async (expenseId, duplicateWarningFingerprint) => {
    suppressRealtimeEvent("expense.changed", expenseId);
    set({ actionLoading: true, actionError: null });
    try {
      const result = await expenseApi.confirm(expenseId, duplicateWarningFingerprint);
      set({ selectedExpense: result.expense });
      if (result.confirmed)
        await refreshExpenseSurfaces(get, result.expense);
      return result;
    } catch (error) {
      set({ actionError: error instanceof Error ? error.message : "Unable to confirm expense" });
      throw error;
    } finally {
      set({ actionLoading: false });
    }
  },
  reject: async (expenseId) => { await runAction(set, get, expenseId, () => expenseApi.reject(expenseId), true); },
  voidExpense: async (expenseId) => { await runAction(set, get, expenseId, () => expenseApi.void(expenseId)); },
  correct: async (expenseId) => {
    suppressRealtimeEvent("expense.changed", expenseId);
    set({ actionLoading: true, actionError: null });
    try {
      const original = findExpense(get(), expenseId);
      const replacement = await expenseApi.correct(expenseId);
      await refreshExpenseSurfaces(get, original, replacement);
      return replacement;
    } catch (error) {
      set({ actionError: error instanceof Error ? error.message : "Unable to correct expense" });
      throw error;
    } finally {
      set({ actionLoading: false });
    }
  },
  uploadAttachment: async (expenseId, file, refreshDetail = true, creationOperationId) => {
    suppressRealtimeEvent("expense.changed", expenseId);
    const attachment = await (creationOperationId ? expenseApi.uploadAttachment(expenseId, file, creationOperationId) : expenseApi.uploadAttachment(expenseId, file));
    if (refreshDetail && get().selectedExpense?.id === expenseId) {
      await get().loadDetail(expenseId);
    }
    return attachment;
  },
  replaceAttachment: async (expenseId, attachmentId, file) => {
    suppressRealtimeEvent("expense.changed", expenseId);
    const attachment = await expenseApi.replaceAttachment(
      expenseId,
      attachmentId,
      file,
    );
    if (get().selectedExpense?.id === expenseId) {
      await get().loadDetail(expenseId);
    }
    return attachment;
  },
  removeAttachment: async (expenseId, attachmentId) => {
    suppressRealtimeEvent("expense.changed", expenseId);
    const expense = await expenseApi.removeAttachment(expenseId, attachmentId);
    set((state) => ({
      selectedExpense:
        state.selectedExpense?.id === expenseId
          ? expense
          : state.selectedExpense,
      expenses: state.expenses.map((item) =>
        item.id === expenseId ? expense : item,
      ),
    }));
  },
  extractAttachment: async (expenseId, attachmentId) => {
    suppressRealtimeEvent("expense.changed", expenseId);
    await expenseApi.extractAttachment(expenseId, attachmentId);
    if (get().selectedExpense?.id === expenseId) {
      await get().loadDetail(expenseId);
    }
  },
  createSchedule: async (payload) => {
    set({ scheduleActionId: "new", actionError: null });
    try {
      const schedule = await expenseApi.createSchedule(payload);
      set((state) => ({ schedules: replaceSchedule(state.schedules, schedule) }));
      await Promise.allSettled([
        ...(get().hydrated ? [get().refresh()] : []),
        get().refreshSummary(),
      ]);
      return schedule;
    } catch (error) {
      set({ actionError: actionError(error, "Unable to create schedule") });
      throw error;
    } finally { set({ scheduleActionId: null }); }
  },
  updateSchedule: async (id, payload, options) => {
    suppressRealtimeEvent("expense.changed", id);
    set({ scheduleActionId: id, actionError: null });
    try {
      const schedule = options?.updateDraftExpenses
        ? await expenseApi.updateSchedule(id, payload, true)
        : await expenseApi.updateSchedule(id, payload);
      set((state) => ({ schedules: replaceSchedule(state.schedules, schedule) }));
      const changesSummary =
        "nextDueDate" in payload ||
        "calculationType" in payload ||
        "fixedAmountCents" in payload ||
        "shippingCents" in payload ||
        "rateBasisPoints" in payload ||
        "lineItems" in payload ||
        "telemetryId" in payload ||
        "rateGroups" in payload;
      // The mutation response is authoritative. Refresh other surfaces in the
      // background so their network latency does not hold the save dialog open.
      void Promise.allSettled([
        ...(options?.updateDraftExpenses || changesSummary
          ? [get().refreshSummary()]
          : []),
        ...((get().hydrated && (options?.updateDraftExpenses || "nextDueDate" in payload)) || get().activeParams.search
          ? [get().refresh()]
          : []),
      ]);
      return schedule;
    } catch (error) {
      set({ actionError: actionError(error, "Unable to update schedule") });
      throw error;
    } finally { set({ scheduleActionId: null }); }
  },
  previewScheduleDraftUpdate: async (id) => {
    const preview = await expenseApi.scheduleDraftUpdatePreview(id);
    return preview.draftCount;
  },
  activateSchedule: async (id) => runScheduleAction(set, get, id, () => expenseApi.activateSchedule(id)),
  deactivateSchedule: async (id) => runScheduleAction(set, get, id, () => expenseApi.deactivateSchedule(id)),
  retryScheduleGeneration: async (id) => {
    const schedule = await runScheduleAction(
      set,
      get,
      id,
      () => expenseApi.retryScheduleGeneration(id),
    );
    await get().refresh();
    return schedule;
  },
  refreshSchedules: async () => set({ schedules: await expenseApi.schedules() }),
  clearActionError: () => set({ actionError: null }),
  applyChanged: async (expenseId, reason, scheduleId) => {
    if (reason && EXPENSE_SCHEDULE_REASONS.has(reason)) {
      await Promise.all([
        get().refreshSchedules(),
        get().refreshSummary(),
        ...(get().activeParams.search ? [get().refresh()] : []),
      ]);
      return;
    }
    if (reason && EXPENSE_DETAIL_ONLY_REASONS.has(reason)) {
      if (reason === "description_edited") {
        set((state) => ({
          expenseQueryVersion: state.expenseQueryVersion + 1,
        }));
      }
      const updates: Promise<unknown>[] = [];
      if (expenseId && get().selectedExpense?.id === expenseId) {
        updates.push(get().loadDetail(expenseId));
      }
      if (reason === "description_edited" && get().activeParams.search) {
        updates.push(get().refresh());
      }
      await Promise.all(updates);
      return;
    }
    await Promise.all([
      get().refresh(),
      get().refreshSummary(),
      ...(scheduleId ? [get().loadSchedule(scheduleId, true)] : []),
      ...(expenseId && get().selectedExpense?.id === expenseId ? [get().loadDetail(expenseId)] : []),
    ]);
  },
  reset: () => {
    listRequest.reset();
    moreRequest.reset();
    detailRequest.reset();
    summaryRequestVersion += 1;
    scheduleRequestVersion += 1;
    scheduleRequests.clear();
    resetExpenseListCacheRequests();
    resetExpenseReferenceRequests();
    resetMerchantReportCache();
    const expenseQueryVersion = get().expenseQueryVersion;
    set({
      expenses: [], categories: [], paymentAccounts: [], schedules: [],
      summary: null, selectedExpense: null, activeParams: {}, statusTotals: [],
      nextCursor: null, hydrated: false, loading: false, loadingMore: false,
      detailLoading: false, summaryLoading: false, actionLoading: false,
      scheduleActionId: null, scheduleLoadingId: null, error: null,
      detailError: null, actionError: null, scheduleError: null,
      expenseQueryVersion,
      ...expenseListCacheInitialState,
      ...expenseReferenceInitialState,
    });
  },
}));

export const replaceSchedule = (schedules: ExpenseSchedule[], schedule: ExpenseSchedule) =>
  schedules.some((item) => item.id === schedule.id)
    ? schedules.map((item) =>
        item.id === schedule.id
          ? {
              ...schedule,
              historySummary:
                schedule.historySummary ?? item.historySummary,
            }
          : item,
      )
    : [...schedules, schedule];

const actionError = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

async function runScheduleAction(
  set: (state: Partial<ExpenseState> | ((state: ExpenseState) => Partial<ExpenseState>)) => void,
  get: () => ExpenseState,
  id: string,
  action: () => Promise<ExpenseSchedule>,
) {
  suppressRealtimeEvent("expense.changed", id);
  set({ scheduleActionId: id, actionError: null });
  try {
    const schedule = await action();
    set((state) => ({ schedules: replaceSchedule(state.schedules, schedule) }));
    await get().refreshSummary().catch(() => undefined);
    return schedule;
  } catch (error) {
    set({ actionError: actionError(error, "Unable to update schedule") });
    throw error;
  } finally { set({ scheduleActionId: null }); }
}

async function runAction(
  set: (state: Partial<ExpenseState>) => void,
  get: () => ExpenseState,
  expenseId: string,
  action: () => Promise<Expense>,
  refreshSchedules = false,
) {
  suppressRealtimeEvent("expense.changed", expenseId);
  set({ actionLoading: true, actionError: null });
  try {
    const expense = await action();
    set({ selectedExpense: expense });
    if (refreshSchedules) {
      await Promise.allSettled([
        get().refresh(),
        get().refreshSummary(),
        get().refreshSchedules(),
      ]);
    } else {
      await refreshExpenseSurfaces(get, expense);
    }
  } catch (error) {
    set({ actionError: error instanceof Error ? error.message : "Unable to update expense" });
    throw error;
  } finally { set({ actionLoading: false }); }
}

const expenseScheduleId = (expense?: Expense | null) =>
  expense?.scheduleId || expense?.schedule?.id || null;

const findExpense = (state: ExpenseState, expenseId: string) =>
  state.selectedExpense?.id === expenseId
    ? state.selectedExpense
    : state.expenses.find((expense) => expense.id === expenseId) || null;

async function refreshExpenseSurfaces(
  get: () => ExpenseState,
  ...expenses: Array<Expense | null | undefined>
) {
  const scheduleIds = Array.from(
    new Set(
      expenses
        .map(expenseScheduleId)
        .filter((scheduleId): scheduleId is string => Boolean(scheduleId)),
    ),
  );
  await Promise.allSettled([
    get().refresh(),
    get().refreshSummary(),
    ...scheduleIds.map((scheduleId) => get().loadSchedule(scheduleId, true)),
  ]);
}
